import { useQuery } from "@tanstack/react-query";
import { 
  Building2, Users, LayoutGrid, Server, 
  ChevronRight, CheckCircle2, AlertTriangle, XCircle, Activity,
  ShieldAlert, FileText, ArrowRight, RefreshCw, MailCheck, Shield, Gauge
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PlatformLayout } from "./platform-layout";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";

interface GeoUsageStats {
  provider: "ipinfo" | "other";
  monthKey: string;
  count: number;
  limit: number;
  pct: number;
}

interface DashboardData {
  totalWorkspaces: number;
  totalUsers: number;
  tierBreakdown: Array<{ tier: string; count: number }>;
  environment: "sandbox" | "production";
  lastDeploy: string;
}

interface AuditEntry {
  id: string;
  action: string;
  actorEmail: string;
  targetWorkspaceId?: string;
  createdAt: string;
}

type HealthStatus = "Healthy" | "Degraded" | "Down";

interface HealthCheck {
  label: string;
  status: HealthStatus;
  detail?: string;
}

interface HealthData {
  checks: HealthCheck[];
  timestamp: string;
}

function HealthIcon({ status }: { status: HealthStatus }) {
  if (status === "Healthy") return <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />;
  if (status === "Degraded") return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
  return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
}

function HealthBadge({ status }: { status: HealthStatus }) {
  if (status === "Healthy")
    return <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Healthy</span>;
  if (status === "Degraded")
    return <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Degraded</span>;
  return <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Down</span>;
}

const TIER_COLORS: Record<string, string> = {
  starter:     "#6366f1",
  growth:      "#3b82f6",
  professional:"#0ea5e9",
  enterprise:  "#10b981",
  free:        "#94a3b8",
};

function actionLabel(action: string) {
  const map: Record<string, string> = {
    workspace_created:      "Workspace created",
    workspace_deleted:      "Workspace deleted",
    user_added:             "User added",
    user_removed:           "User removed",
    tier_changed:           "Permission tier changed",
    audit_export:           "Audit log exported",
    backup_completed:       "Backup completed",
    impersonation_start:    "Impersonation started",
    impersonation_stop:     "Impersonation stopped",
    platform_login:         "Admin logged in",
    platform_logout:        "Admin logged out",
  };
  return map[action] ?? action.replace(/_/g, " ");
}

function DonutChart({ data }: { data: Array<{ tier: string; count: number }> }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-36 text-sm text-gray-400">
        No workspace data
      </div>
    );
  }

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const slices = data.map((d) => {
    const pct = d.count / total;
    const dash = pct * circumference;
    const slice = { tier: d.tier, count: d.count, pct, dash, offset };
    offset += dash;
    return slice;
  });

  return (
    <div className="flex flex-col items-center gap-4">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="20" />
        {slices.map((s) => (
          <circle
            key={s.tier}
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke={TIER_COLORS[s.tier] ?? "#94a3b8"}
            strokeWidth="20"
            strokeDasharray={`${s.dash} ${circumference - s.dash}`}
            strokeDashoffset={-(s.offset - circumference / 4)}
            strokeLinecap="butt"
          />
        ))}
        <text x="70" y="65" textAnchor="middle" className="text-lg" fontSize="22" fontWeight="700" fill="#111827">{total}</text>
        <text x="70" y="83" textAnchor="middle" fontSize="11" fill="#6b7280">workspaces</text>
      </svg>
      <div className="w-full space-y-1.5">
        {slices.map((s) => (
          <div key={s.tier} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: TIER_COLORS[s.tier] ?? "#94a3b8" }}
              />
              <span className="capitalize text-gray-700">{s.tier}</span>
            </div>
            <span className="text-gray-500 font-medium">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PlatformDashboard() {
  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ["/api/platform/dashboard"],
  });

  const { data: auditData } = useQuery<AuditEntry[]>({
    queryKey: ["/api/platform/audit-log"],
    select: (rows) => rows.slice(0, 6),
  });

  const { data: healthData, isLoading: healthLoading, isFetching: healthFetching, isError: healthError, dataUpdatedAt, refetch: refetchHealth } = useQuery<HealthData>({
    queryKey: ["/api/platform/health"],
    refetchInterval: 60_000,
  });

  const tierBreakdown = data?.tierBreakdown ?? [];
  const totalTierCount = tierBreakdown.reduce((s, t) => s + t.count, 0);

  const { data: ipCheck } = useQuery<{ currentIp: string | null; isAllowlisted: boolean | null; allowlistConfigured: boolean }>({
    queryKey: ["/api/platform/ip-check"],
    staleTime: 5 * 60 * 1000,
  });

  const { data: geoUsage, isLoading: geoUsageLoading } = useQuery<GeoUsageStats>({
    queryKey: ["/api/platform/ipinfo-usage"],
    refetchInterval: 5 * 60 * 1000,
  });

  const showIpBanner = ipCheck?.allowlistConfigured && ipCheck?.isAllowlisted === false;

  return (
    <PlatformLayout>
      <div className="p-6 space-y-6 max-w-[1400px]">
        {/* IP allowlist warning banner (Task #53) */}
        {showIpBanner && (
          <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30" data-testid="alert-ip-not-allowlisted">
            <Shield className="h-4 w-4 text-amber-600 mt-0.5" />
            <AlertDescription className="flex flex-wrap items-center gap-3">
              <span className="text-amber-800 dark:text-amber-300">
                You're signed in from{" "}
                <code className="font-mono font-semibold">{ipCheck?.currentIp}</code>, which is
                not on the IP allowlist. Next time you sign in from this IP you'll need to pass
                dual-factor verification (SMS + email).
              </span>
              <Link href="/platform/ip-allowlist">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500 text-amber-700 hover:bg-amber-100 shrink-0"
                  data-testid="button-banner-add-ip"
                >
                  Add this IP to allowlist
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Page heading */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Dashboard</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Overview of all workspaces and platform health
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800" />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Workspaces</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="stat-workspaces">
                        {data?.totalWorkspaces ?? 0}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Active tenants</p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Users</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="stat-users">
                        {data?.totalUsers ?? 0}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Across all workspaces</p>
                    </div>
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <Users className="h-5 w-5 text-indigo-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Tier Distribution</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {tierBreakdown.length}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {tierBreakdown.map((t) => (
                          <span key={t.tier} className="text-xs text-gray-400 capitalize">{t.tier}:{t.count}</span>
                        ))}
                      </div>
                    </div>
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <LayoutGrid className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Environment</p>
                      <div className="mt-2">
                        <Badge
                          className={
                            data?.environment === "production"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-yellow-100 text-yellow-800 border-yellow-200"
                          }
                          data-testid="badge-environment"
                        >
                          {data?.environment ?? "sandbox"}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Deploy: {data?.lastDeploy ? new Date(data.lastDeploy).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <Server className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-3">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  {
                    href: "/platform/workspaces",
                    icon: Building2,
                    iconBg: "bg-blue-50",
                    iconColor: "text-blue-600",
                    title: "Manage Workspaces",
                    subtitle: "View and configure tenants",
                    testId: "link-manage-workspaces",
                  },
                  {
                    href: "/platform/tiers",
                    icon: LayoutGrid,
                    iconBg: "bg-purple-50",
                    iconColor: "text-purple-600",
                    title: "Permission Tiers",
                    subtitle: "Configure tier limits",
                    testId: "link-permission-tiers",
                  },
                  {
                    href: "/platform/audit",
                    icon: FileText,
                    iconBg: "bg-gray-100",
                    iconColor: "text-gray-600",
                    title: "Audit Log",
                    subtitle: "Review platform activity",
                    testId: "link-audit-log",
                  },
                  {
                    href: "/platform/seed-failure-alerts",
                    icon: ShieldAlert,
                    iconBg: "bg-red-50",
                    iconColor: "text-red-600",
                    title: "Security Alerts",
                    subtitle: "View active platform alerts",
                    testId: "link-security-alerts",
                  },
                  {
                    href: "/platform/email-verify",
                    icon: MailCheck,
                    iconBg: "bg-blue-50",
                    iconColor: "text-blue-600",
                    title: "Email Verification",
                    subtitle: "NeverBounce bulk-verify workspace emails",
                    testId: "link-email-verify",
                  },
                ].map((action) => (
                  <a
                    key={action.href}
                    href={action.href}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                    data-testid={action.testId}
                  >
                    <div className={`p-2.5 rounded-lg ${action.iconBg} shrink-0`}>
                      <action.icon className={`h-5 w-5 ${action.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{action.title}</div>
                      <div className="text-xs text-gray-400 truncate">{action.subtitle}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
                  </a>
                ))}
              </div>
            </div>

            {/* API Quotas */}
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-3">API Quotas</h2>
              <Card className="border border-gray-200 shadow-sm" data-testid="card-api-quotas">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Gauge className="h-4 w-4 text-gray-500" />
                      IPInfo geo-lookup
                    </CardTitle>
                    <span className="text-xs text-gray-400">
                      {geoUsage?.monthKey ?? "—"}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {geoUsageLoading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-2 rounded-full bg-gray-100" />
                      <div className="h-3 w-32 bg-gray-100 rounded" />
                    </div>
                  ) : geoUsage?.provider !== "ipinfo" ? (
                    <p className="text-xs text-gray-400">
                      IPInfo is not the active geo provider — no quota to track.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {/* Progress bar */}
                      <div className="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            (geoUsage.pct ?? 0) >= 80
                              ? "bg-red-500"
                              : (geoUsage.pct ?? 0) >= 60
                              ? "bg-amber-500"
                              : "bg-emerald-500"
                          }`}
                          style={{ width: `${Math.min(100, geoUsage.pct ?? 0)}%` }}
                          data-testid="progress-ipinfo-usage"
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">
                          <span className="font-semibold">{(geoUsage.count ?? 0).toLocaleString()}</span>
                          {" / "}
                          {(geoUsage.limit ?? 50000).toLocaleString()} req
                        </span>
                        <span
                          className={`font-semibold ${
                            (geoUsage.pct ?? 0) >= 80
                              ? "text-red-600"
                              : (geoUsage.pct ?? 0) >= 60
                              ? "text-amber-600"
                              : "text-emerald-600"
                          }`}
                          data-testid="text-ipinfo-pct"
                        >
                          {(geoUsage.pct ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      {(geoUsage.pct ?? 0) >= 80 && (
                        <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          Over 80 % — you will receive an alert email
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        Free tier · 50 k req / month · resets 1st of each month
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Bottom 3-column section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Platform Health */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-gray-900">Platform Health</CardTitle>
                    <div className="flex items-center gap-2">
                      {dataUpdatedAt > 0 && (
                        <span className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true })}
                        </span>
                      )}
                      <button
                        onClick={() => refetchHealth()}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        data-testid="button-refresh-health"
                        title="Refresh health checks"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${healthFetching ? "animate-spin" : ""}`} />
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {healthLoading ? (
                    <div className="space-y-3">
                      {["Database","API Services","Background Jobs","Storage","Email Delivery"].map((label) => (
                        <div key={label} className="flex items-center justify-between animate-pulse">
                          <div className="flex items-center gap-2.5">
                            <div className="h-4 w-4 rounded-full bg-gray-100 shrink-0" />
                            <span className="text-sm text-gray-300">{label}</span>
                          </div>
                          <div className="h-5 w-14 rounded-full bg-gray-100" />
                        </div>
                      ))}
                    </div>
                  ) : healthError ? (
                    <div className="flex items-center gap-2 py-2" data-testid="health-error">
                      <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                      <span className="text-sm text-gray-500">Health check unavailable</span>
                    </div>
                  ) : (healthData?.checks ?? []).length === 0 ? (
                    <p className="text-sm text-gray-400 py-2" data-testid="health-empty">No health data returned</p>
                  ) : (
                    (healthData!.checks).map((check) => (
                      <div key={check.label} className="flex items-center justify-between" data-testid={`health-check-${check.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="flex items-center gap-2.5">
                          <HealthIcon status={check.status} />
                          <div>
                            <span className="text-sm text-gray-700">{check.label}</span>
                            {check.detail && (
                              <p className="text-xs text-gray-400">{check.detail}</p>
                            )}
                          </div>
                        </div>
                        <HealthBadge status={check.status} />
                      </div>
                    ))
                  )}
                  <a
                    href="/platform/seed-failure-alerts"
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                    data-testid="link-view-system-status"
                  >
                    View System Status
                    <ArrowRight className="h-3 w-3" />
                  </a>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-gray-900">Recent Activity</CardTitle>
                    <FileText className="h-4 w-4 text-gray-400" />
                  </div>
                </CardHeader>
                <CardContent>
                  {!auditData || auditData.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No recent activity</p>
                  ) : (
                    <div className="space-y-3">
                      {auditData.map((entry) => (
                        <div key={entry.id} className="flex items-start gap-3">
                          <div className="mt-0.5 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                            <Activity className="h-3 w-3 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 capitalize leading-tight">
                              {actionLabel(entry.action)}
                            </p>
                            <p className="text-xs text-gray-400 truncate">{entry.actorEmail}</p>
                          </div>
                          <span className="text-xs text-gray-400 shrink-0 mt-0.5">
                            {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <a
                    href="/platform/audit"
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-3"
                    data-testid="link-view-full-audit"
                  >
                    View full audit log
                    <ArrowRight className="h-3 w-3" />
                  </a>
                </CardContent>
              </Card>

              {/* Workspace Summary */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-gray-900">Workspace Summary</CardTitle>
                    <Building2 className="h-4 w-4 text-gray-400" />
                  </div>
                </CardHeader>
                <CardContent>
                  <DonutChart data={tierBreakdown} />
                  <a
                    href="/platform/workspaces"
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-4"
                    data-testid="link-manage-workspaces-summary"
                  >
                    Manage Workspaces
                    <ArrowRight className="h-3 w-3" />
                  </a>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </PlatformLayout>
  );
}
